import gql from 'graphql-tag'
import { connect, batch } from 'react-redux'
import { compose } from 'recompose'

import {
  dataFetched,
  dataRemoved,
  makesQueries,
  extraDataFromResultAndDispatch,
  makesMutations,
  makesSubscriptions,
} from 'data/graphql'

import { trackNavigateToDoc } from 'data/analytics/actions'

import { channelAttrsFragment } from 'remote/channels'
import { threadAttrsWithCommentsFragment } from 'remote/threads'

import {
  threadCreatedSubscription,
  threadUpdatedSubscription,
  threadArchivedSubscription,
  commentAddedToThreadSubscription,
  commentUpdatedSubscription,
  commentArchivedSubscription,
} from 'remote/threads'

import { threadAknowledgementFragment } from 'remote/activityRegistry'

import {
  noteAttrsFragment,
  noteBaseAttributesFragment,
  updateNote,
  publiclyShareNote,
  publiclyUnshareNote,
  makePublicNoteEditable,
  makePublicNoteNonEditable,
  updateNotePositionInChannel,
} from 'remote/notes'

import {
  viewIsLoading,
  viewHasLoaded,
  viewFailedToLoad,
  injectViewStateAsMeta,
} from 'data/ui/viewState'

export const noteQuery = gql`
  query showNote($noteId: ID!, $track: Boolean) {
    showNote(noteId: $noteId, track: $track) {
      ...noteAttrs
      parentNote {
        ...noteAttrs
        parentNote {
          ...noteAttrs
          parentNote {
            ...noteAttrs
            parentNote {
              ...noteAttrs
            }
          }
        }
      }
      channel {
        ...channelAttrs
      }
    }
    listNoteThreads(noteId: $noteId) {
      ...threadAttrsWithComments
    }
    listCurrentUserThreadActivityAknowledgments(noteId: $noteId) {
      ...threadAknowledgementAttrs
    }
  }
  ${noteAttrsFragment}
  ${channelAttrsFragment}
  ${threadAttrsWithCommentsFragment}
  ${threadAknowledgementFragment}
`

export const noteByTokenQuery = gql`
  query showNoteByToken($token: ID!) {
    showNoteByToken(token: $token) {
      ...noteAttrs
    }
  }
  ${noteAttrsFragment}
`

const syncsNote = ({
  track = false,
  mapPropsToVariables = ({ noteId }) => ({ noteId, track }),
  viewName,
  onNoteFetch = () => {},
  onNoteFetchError = () => {},
}) =>
  compose(
    connect(
      null,
      {
        dataFetched,
        dataRemoved,
        viewIsLoading,
        viewFailedToLoad,
        injectViewStateAsMeta,
        trackNavigateToDoc,
      }
    ),
    makesQueries({
      query: noteQuery,
      batch: false,
      resyncPolicy: 'refetch',
      mapPropsToVariables,
      getOnQueryFromProps: ({ viewIsLoading }) => () =>
        viewIsLoading(viewName, 'loadNote'),
      getOnResultFromProps: props => ({ result, meta }) => {
        const {
          showNote,
          listNoteThreads,
          listCurrentUserThreadActivityAknowledgments,
        } = result.data

        const data = [
          showNote,
          ...listNoteThreads,
          ...listCurrentUserThreadActivityAknowledgments,
        ]

        batch(() => {
          props.injectViewStateAsMeta(
            dataFetched(data, meta),
            viewHasLoaded(viewName, 'loadNote')
          )
          onNoteFetch(props, showNote)
          props.trackNavigateToDoc()
        })
      },
      getOnErrorFromProps: props => ({ error }) => {
        const { viewFailedToLoad } = props
        viewFailedToLoad(viewName, 'loadNote', error)
        onNoteFetchError(props, error)
      },
    }),
    makesSubscriptions({
      query: threadCreatedSubscription,
      mapPropsToVariables,
      getOnResultFromProps: ({ dataFetched }) => ({ result, meta }) => {
        dataFetched(result.data.threadCreated, meta)
      },
    }),
    makesSubscriptions({
      query: commentAddedToThreadSubscription,
      mapPropsToVariables,
      getOnResultFromProps: ({ dataFetched }) => ({ result, meta }) => {
        dataFetched(result.data.commentAddedToThread, meta)
      },
    }),
    makesSubscriptions({
      query: threadUpdatedSubscription,
      mapPropsToVariables,
      getOnResultFromProps: ({ dataFetched }) => ({ result, meta }) => {
        dataFetched(result.data.threadUpdated, meta)
      },
    }),
    makesSubscriptions({
      query: threadArchivedSubscription,
      mapPropsToVariables,
      getOnResultFromProps: ({ dataRemoved }) => ({ result, meta }) => {
        dataRemoved(
          {
            id: result.data.threadArchived.threadId,
            __typename: 'Thread',
          },
          meta
        )
      },
    }),
    makesSubscriptions({
      query: commentUpdatedSubscription,
      mapPropsToVariables,
      getOnResultFromProps: ({ dataFetched }) => ({ result, meta }) => {
        dataFetched(result.data.commentUpdated.comment, meta)
      },
    }),
    makesSubscriptions({
      query: commentArchivedSubscription,
      mapPropsToVariables,
      getOnResultFromProps: ({ dataRemoved }) => ({ result, meta }) => {
        dataRemoved(
          { id: result.data.commentArchived.commentId, __typename: 'Comment' },
          meta
        )
      },
    })
  )
export default syncsNote

export const providesNoteMutations = compose(
  connect(
    null,
    {
      dataFetched,
    }
  ),
  makesMutations({
    query: updateNote,
    mutationPropName: 'updateNote',
    getOnResultFromProps: extraDataFromResultAndDispatch('updateNote'),
  }),
  makesMutations({
    query: updateNotePositionInChannel,
    mutationPropName: 'updateNotePositionInChannel',
    getOnResultFromProps: extraDataFromResultAndDispatch(
      'updateNotePositionInChannel'
    ),
  }),
  makesMutations({
    query: publiclyShareNote,
    mutationPropName: 'publiclyShareNote',
    getOnResultFromProps: extraDataFromResultAndDispatch('publiclyShareNote'),
  }),
  makesMutations({
    query: publiclyUnshareNote,
    mutationPropName: 'publiclyUnshareNote',
    getOnResultFromProps: extraDataFromResultAndDispatch('publiclyUnshareNote'),
  }),
  makesMutations({
    query: makePublicNoteEditable,
    mutationPropName: 'makePublicNoteEditable',
    getOnResultFromProps: extraDataFromResultAndDispatch(
      'makePublicNoteEditable'
    ),
  }),
  makesMutations({
    query: makePublicNoteNonEditable,
    mutationPropName: 'makePublicNoteNonEditable',
    getOnResultFromProps: extraDataFromResultAndDispatch(
      'makePublicNoteNonEditable'
    ),
  }),
  makesMutations({
    query: gql`
      mutation MoveNotesTo(
        $ids: [ID!]!
        $channelId: ID!
        $parentNoteId: ID
        $position: Float
      ) {
        moveNotesTo(
          ids: $ids
          channelId: $channelId
          parentNoteId: $parentNoteId
          position: $position
        ) {
          ...noteAttrs
        }
      }
      ${noteAttrsFragment}
    `,
    mutationPropName: 'moveNotesTo',
    getOnResultFromProps: extraDataFromResultAndDispatch('moveNotesTo'),
  }),
  makesMutations({
    query: gql`
      mutation setNoteIcon($id: ID!, $input: NoteIconInput!) {
        setNoteIcon(id: $id, input: $input) {
          ...noteBaseAttributes
        }
      }
      ${noteBaseAttributesFragment}
    `,
    mutationPropName: 'setNoteIcon',
    getOnResultFromProps: extraDataFromResultAndDispatch('setNoteIcon'),
  })
)
